import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, ArrowLeft, Camera, X, CheckCircle2, Clock } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FullLoader, Spinner } from "../components/common";
import { Logo } from "../components/Logo";

const MAX_PHOTOS = 4;

export default function Appeal() {
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState(null);
  const [explanation, setExplanation] = useState("");
  const [photos, setPhotos] = useState([]); // {file, url}
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/account/guard-status"); setState(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    const next = [...photos];
    for (const f of files) {
      if (next.length >= MAX_PHOTOS) break;
      next.push({ file: f, url: URL.createObjectURL(f) });
    }
    setPhotos(next);
    if (fileRef.current) fileRef.current.value = "";
  };
  const removePhoto = (i) => setPhotos(photos.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (explanation.trim().length < 10) { toast.error("Please explain your situation (at least 10 characters)."); return; }
    if (photos.length < 1) { toast.error("Attach at least one photo."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("explanation", explanation.trim());
      photos.forEach((p) => fd.append("photos", p.file));
      await api.post("/account/appeal", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Appeal submitted — our team will review it shortly.");
      setPhotos([]); setExplanation("");
      await load();
      refreshUser?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!state) return <FullLoader />;

  const banned = state.banned;
  const pending = state.latest_appeal && state.latest_appeal.status === "pending";

  return (
    <div className="min-h-[100dvh] bg-mesh" style={{ paddingBottom: "max(2rem, env(safe-area-inset-bottom))" }}>
      <div className="max-w-md mx-auto px-5 pt-6">
        <div className="flex items-center justify-between mb-6">
          <button data-testid="appeal-back-btn" onClick={() => nav("/app")} className="grid place-items-center w-10 h-10 rounded-full bg-secondary">
            <ArrowLeft size={18} />
          </button>
          <Logo size={32} />
          <span className="w-10" />
        </div>

        {/* Status card */}
        <div className={`rounded-3xl p-6 text-white mb-6 shadow-lg ${banned ? "bg-gradient-to-br from-red-600 to-red-800" : "bg-gradient-to-br from-amber-500 to-orange-600"}`}
          data-testid="appeal-status-card">
          <ShieldAlert size={30} className="mb-3" />
          <h1 className="font-display text-2xl font-semibold">
            {banned ? "Account banned" : "Account restricted"}
          </h1>
          <p className="text-white/85 text-sm mt-1">
            {banned
              ? `Your account reached ${state.max_restrictions} restrictions and has been banned.`
              : `You have ${state.restriction_count} of ${state.max_restrictions} restrictions.`}
          </p>
          <div className="mt-4 flex gap-1.5">
            {Array.from({ length: state.max_restrictions }).map((_, i) => (
              <span key={i} className={`h-1.5 flex-1 rounded-full ${i < state.restriction_count ? "bg-white" : "bg-white/25"}`} />
            ))}
          </div>
        </div>

        {/* Active restrictions */}
        {state.restrictions?.length > 0 && (
          <div className="rounded-2xl border border-border bg-card p-4 mb-6" data-testid="appeal-reasons">
            <h2 className="text-sm font-semibold mb-2">Why your account was restricted</h2>
            <ul className="space-y-2">
              {state.restrictions.map((r, i) => (
                <li key={i} className="text-sm text-muted-foreground flex gap-2">
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span>{r.reason}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pending appeal */}
        {pending ? (
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5 text-center" data-testid="appeal-pending">
            <Clock size={28} className="mx-auto text-primary mb-2" />
            <h2 className="font-display font-semibold">Appeal under review</h2>
            <p className="text-sm text-muted-foreground mt-1">
              We received your appeal and our team is reviewing it. You'll get a notification with the decision.
            </p>
          </div>
        ) : state.latest_appeal && state.latest_appeal.status === "rejected" ? (
          <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5 mb-5" data-testid="appeal-rejected">
            <h2 className="font-display font-semibold text-destructive">Previous appeal declined</h2>
            {state.latest_appeal.review_note && (
              <p className="text-sm text-muted-foreground mt-1">Note: {state.latest_appeal.review_note}</p>
            )}
            <p className="text-sm text-muted-foreground mt-1">You may submit a new appeal below.</p>
          </div>
        ) : null}

        {/* Appeal form */}
        {state.can_appeal && !pending && (
          <div data-testid="appeal-form">
            <h2 className="font-display font-semibold text-lg mb-1">Submit an appeal</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Explain your situation and attach up to {MAX_PHOTOS} photos (ID, proof, or anything that supports your case).
            </p>

            <label className="text-sm font-medium">Your explanation</label>
            <textarea data-testid="appeal-explanation" value={explanation} onChange={(e) => setExplanation(e.target.value)} rows={5}
              className="w-full mt-1 mb-4 p-3 rounded-xl bg-secondary outline-none text-sm resize-none"
              placeholder="Tell us what happened and why your account should be reinstated…" />

            <label className="text-sm font-medium">Photos ({photos.length}/{MAX_PHOTOS})</label>
            <div className="grid grid-cols-4 gap-2 mt-2 mb-4">
              {photos.map((p, i) => (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-secondary" data-testid={`appeal-photo-${i}`}>
                  <img src={p.url} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removePhoto(i)} data-testid={`appeal-remove-photo-${i}`}
                    className="absolute top-1 right-1 grid place-items-center w-6 h-6 rounded-full bg-black/60 text-white">
                    <X size={13} />
                  </button>
                </div>
              ))}
              {photos.length < MAX_PHOTOS && (
                <button data-testid="appeal-add-photo" onClick={() => fileRef.current?.click()}
                  className="aspect-square rounded-xl border-2 border-dashed border-border grid place-items-center text-muted-foreground">
                  <Camera size={20} />
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" onChange={addPhotos} className="hidden" />

            <button data-testid="appeal-submit-btn" onClick={submit} disabled={busy}
              className="w-full h-13 py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center disabled:opacity-50">
              {busy ? <Spinner className="w-5 h-5" /> : "Submit appeal"}
            </button>
          </div>
        )}

        {!state.can_appeal && !pending && state.restriction_count === 0 && !banned && (
          <div className="rounded-2xl border border-success/30 bg-success/5 p-5 text-center" data-testid="appeal-clear">
            <CheckCircle2 size={28} className="mx-auto text-success mb-2" />
            <h2 className="font-display font-semibold">Your account is in good standing</h2>
            <p className="text-sm text-muted-foreground mt-1">There are no restrictions to appeal.</p>
          </div>
        )}
      </div>
    </div>
  );
}
